import { forwardRef, type ReactElement, type SelectHTMLAttributes } from 'react';

import { cn } from '@shared/utils';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface FormSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly invalid?: boolean;
  readonly options: SelectOption[];
  readonly placeholder?: string;
}

const inputBaseClass =
  'w-full h-14 rounded-sm border border-[#D5D7DA] bg-white px-3 text-sm text-[#181D26] placeholder:text-[#A4A7AE] outline-hidden transition-[border-color] focus:border-[#00467D] focus:border-2 appearance-none pr-10 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed disabled:border-gray-200';

const inputErrorClass = 'border-2 border-[#F04438]';

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(function FormSelect(
  { className, invalid, options, placeholder, 'aria-invalid': ariaInvalidProp, ...rest },
  ref,
): ReactElement {
  const ariaInvalid = ariaInvalidProp ?? (invalid === true ? ('true' as const) : undefined);

  return (
    <div className="relative">
      <select
        ref={ref}
        aria-invalid={ariaInvalid}
        className={cn(inputBaseClass, invalid === true && inputErrorClass, className)}
        {...rest}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#71767F]">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </div>
  );
});
